import { pick } from "./compose";

export interface NamePool {
  region: string;
  given: readonly string[];
  family: readonly string[];
}

/**
 * 26 worldwide regional pools, each with >= 15 given names and >= 15 family names.
 * Ensures consistent cultural pairing and tens of thousands of combinations.
 */
export const WORLD_REGIONAL_POOLS: readonly NamePool[] = [
  // 1. East Asia (China)
  {
    region: "East Asia (Chinese)",
    given: ["Wei", "Ying", "Jian", "Mei-Ling", "Zhen", "Xiao", "Haoran", "Ruoxi", "Bo", "Ting", "Zhiyuan", "Shu", "Jun", "Qian", "Wen"],
    family: ["Chen", "Lin", "Wang", "Zhang", "Liu", "Yang", "Huang", "Zhao", "Wu", "Xu", "Sun", "Ma", "Zhu", "Guo", "He"],
  },
  // 2. East Asia (Japan)
  {
    region: "East Asia (Japanese)",
    given: ["Ren", "Aoi", "Haruto", "Yui", "Souta", "Hina", "Takumi", "Sakura", "Kenji", "Akari", "Daiki", "Nanami", "Kazuki", "Rin", "Kaito"],
    family: ["Sato", "Suzuki", "Takahashi", "Tanaka", "Watanabe", "Ito", "Nakamura", "Kobayashi", "Yamamoto", "Saito", "Kato", "Yoshida", "Yamada", "Sasaki", "Matsumoto"],
  },
  // 3. East Asia (Korea)
  {
    region: "East Asia (Korean)",
    given: ["Min-jun", "Seo-yeon", "Do-yun", "Ha-eun", "Ji-ho", "Ji-woo", "Jun-seo", "Su-a", "Ye-jun", "Da-eun", "Si-woo", "Chae-won", "Hyun-woo", "Min-seo", "Eun-woo"],
    family: ["Kim", "Lee", "Park", "Jung", "Choi", "Cho", "Kang", "Yoon", "Jang", "Lim", "Han", "Oh", "Seo", "Shin", "Kwon"],
  },
  // 4. South Asia (North India / Hindi)
  {
    region: "South Asia (North)",
    given: ["Aarav", "Ananya", "Vihaan", "Diya", "Rohan", "Ishaan", "Priya", "Aditya", "Sneha", "Kavya", "Arjun", "Mira", "Dev", "Tara", "Nikhil"],
    family: ["Sharma", "Verma", "Kapoor", "Gupta", "Malhotra", "Mehta", "Bhatia", "Chopra", "Joshi", "Bose", "Saxena", "Chauhan", "Pandey", "Mishra", "Aggarwal"],
  },
  // 5. South Asia (South India / Tamil & Telugu)
  {
    region: "South Asia (South)",
    given: ["Karthik", "Samyuktha", "Aravind", "Nandhini", "Pranav", "Lavanya", "Harish", "Shruthi", "Gautham", "Anandhi", "Vignesh", "Meenakshi", "Siddharth", "Pavithra", "Teja"],
    family: ["Iyer", "Rao", "Reddy", "Nair", "Pillai", "Menon", "Sundaram", "Subramanian", "Choudary", "Krishnan", "Venkatesh", "Balasubramanian", "Murthy", "Rangan", "Naidu"],
  },
  // 6. Southeast Asia (Vietnam)
  {
    region: "Southeast Asia (Vietnamese)",
    given: ["Minh", "Linh", "Duc", "Huong", "Phuong", "Thao", "Tuan", "Mai", "Quang", "Trang", "Hoang", "Ngoc", "Khoa", "Anh", "Bao"],
    family: ["Nguyen", "Tran", "Le", "Pham", "Hoang", "Huynh", "Phan", "Vu", "Dang", "Bui", "Do", "Ho", "Ngo", "Duong", "Ly"],
  },
  // 7. Southeast Asia (Indonesia & Malaysia)
  {
    region: "Southeast Asia (Malay / Indonesian)",
    given: ["Budi", "Siti", "Rizky", "Nur", "Arif", "Dewi", "Agus", "Putri", "Hafiz", "Ayu", "Fikri", "Lestari", "Taufiq", "Mega", "Bayu"],
    family: ["Wijaya", "Kusuma", "Santoso", "Saputra", "Utomo", "Setiawan", "Pratama", "Lubis", "Siregar", "Firmansyah", "Hidayat", "Nasution", "Suryadi", "Rahman", "Wahyudi"],
  },
  // 8. Southeast Asia (Philippines)
  {
    region: "Southeast Asia (Filipino)",
    given: ["Mateo", "Tala", "Danilo", "Mayumi", "Isagani", "Luningning", "Bayani", "Dalisay", "Makisig", "Hiraya", "Alon", "Amihan", "Lakan", "Marikit", "Dakila"],
    family: ["Santos", "Reyes", "Cruz", "Bautista", "Ocampo", "Garcia", "Mendoza", "Torres", "Tomas", "Castillo", "Villanueva", "Ramos", "Aquino", "Mercado", "De la Cruz"],
  },
  // 9. Central Asia (Kazakh / Uzbek)
  {
    region: "Central Asia",
    given: ["Alikhan", "Aruzhan", "Nursultan", "Madina", "Timur", "Zhuldyz", "Rustam", "Dilnoza", "Sanjar", "Kamila", "Eldar", "Aygerim", "Bekzod", "Gulnara", "Baurzhan"],
    family: ["Nazarbayev", "Suleimenov", "Karimov", "Tursunov", "Kasymov", "Rakhimov", "Abdullayev", "Ospanov", "Jumaniyazov", "Yusupov", "Akhmetov", "Kenzhebayev", "Alimov", "Umarov", "Ismailov"],
  },
  // 10. Middle East & Levant (Arabic)
  {
    region: "Middle East (Levant / Gulf)",
    given: ["Zayd", "Layla", "Tariq", "Fatima", "Karim", "Noor", "Faris", "Maryam", "Omar", "Yara", "Samir", "Rania", "Bilal", "Salma", "Hassan"],
    family: ["Al-Mansoor", "Haddad", "Khoury", "Al-Najjar", "Darwish", "Saleh", "Al-Hashemi", "Bishara", "Nassar", "Sarkis", "Al-Sabah", "Ghanem", "Kabbara", "Barakat", "Al-Masri"],
  },
  // 11. North Africa (Maghreb / Amazigh)
  {
    region: "North Africa (Maghreb)",
    given: ["Anis", "Amel", "Sofiane", "Kenza", "Youssef", "Ines", "Mehdi", "Yasmina", "Idir", "Numidia", "Aksil", "Kahina", "Massinissa", "Dihya", "Walid"],
    family: ["Benali", "Bouazza", "Amrani", "Mansouri", "Guerfi", "Zouari", "Chraibi", "Belkacem", "Tazi", "Berrada", "Khelifi", "Mebarki", "Dahmani", "Ghezali", "Idrissi"],
  },
  // 12. West Africa (Yoruba)
  {
    region: "West Africa (Yoruba)",
    given: ["Babajide", "Folashade", "Olumide", "Simisola", "Adeyemi", "Morayo", "Damilola", "Kehinde", "Taiwo", "Eniola", "Ayodele", "Bukola", "Olamide", "Tiwa", "Babatunde"],
    family: ["Adeyemi", "Ogunleye", "Balogun", "Oladipo", "Babangida", "Awolowo", "Akinjide", "Soyinka", "Oyekan", "Fashola", "Oshodi", "Adeleke", "Alakija", "Dosunmu", "Olowu"],
  },
  // 13. West Africa (Akan / Ghana)
  {
    region: "West Africa (Akan)",
    given: ["Kofi", "Ama", "Kwame", "Akosua", "Yaw", "Abena", "Kwesi", "Afia", "Kojo", "Yaa", "Ekow", "Akua", "Kwabena", "Adwoa", "Kobina"],
    family: ["Mensah", "Osei", "Appiah", "Boateng", "Agyemang", "Asante", "Darko", "Owusu", "Frimpong", "Annan", "Danquah", "Antwi", "Boakye", "Acheampong", "Sarfo"],
  },
  // 14. East Africa (Swahili / Kenya & Tanzania)
  {
    region: "East Africa (Swahili)",
    given: ["Juma", "Amina", "Baraka", "Zawadi", "Faraji", "Rehema", "Khamisi", "Subira", "Bakari", "Halima", "Kheri", "Imani", "Rashidi", "Zuri", "Amani"],
    family: ["Mwangi", "Kiprono", "Nyerere", "Kamau", "Ochieng", "Kariuki", "Mboya", "Kiprop", "Mutua", "Wanjiku", "Otieno", "Kilonzo", "Maina", "Ndegwa", "Nyambura"],
  },
  // 15. East Africa (Amharic / Ethiopia)
  {
    region: "East Africa (Ethiopian)",
    given: ["Dawit", "Selam", "Yonas", "Liya", "Tewodros", "Bethlehem", "Ephrem", "Marta", "Abebe", "Tigist", "Binyam", "Hanna", "Kaleb", "Rahel", "Haile"],
    family: ["Tadesse", "Bekele", "Alemu", "Gebre", "Assefa", "Worku", "Haile", "Desta", "Tekle", "Mekonnen", "Wolde", "Tesfaye", "Kassahun", "Girma", "Berhanu"],
  },
  // 16. Southern Africa (Nguni / Zulu & Xhosa)
  {
    region: "Southern Africa (Nguni)",
    given: ["Sipho", "Nandi", "Thabo", "Nomvula", "Lindiwe", "Bongani", "Zola", "Mbuso", "Sibusiso", "Buhle", "Mandla", "Ayanda", "Kagiso", "Lerato", "Lungile"],
    family: ["Dlamini", "Nkosi", "Ndlovu", "Khumalo", "Sithole", "Zulu", "Mthembu", "Ngcobo", "Zondi", "Buthelezi", "Khoza", "Hadebe", "Cele", "Mkhize", "Bhengu"],
  },
  // 17. Northern Europe (Nordic)
  {
    region: "Northern Europe (Nordic)",
    given: ["Astrid", "Lars", "Freja", "Magnus", "Sigrid", "Henrik", "Ingrid", "Soren", "Elin", "Torsten", "Karin", "Nils", "Birgitta", "Arvid", "Solveig"],
    family: ["Lindqvist", "Bergstrom", "Holm", "Engstrom", "Nygaard", "Eklund", "Strom", "Lund", "Dahl", "Hansen", "Nilsen", "Sorensen", "Rasmussen", "Lofgren", "Westermark"],
  },
  // 18. Western Europe (Gaelic / Celtic)
  {
    region: "Western Europe (Gaelic)",
    given: ["Rowan", "Maeve", "Cormac", "Fiona", "Declan", "Sorcha", "Callum", "Niamh", "Eamon", "Aisling", "Finnian", "Orla", "Ciaran", "Brigid", "Rory"],
    family: ["Gallagher", "O'Sullivan", "MacLeod", "Doherty", "O'Connor", "Campbell", "Quinn", "MacKenzie", "Brennan", "Connolly", "O'Donnell", "Frazier", "Kavanagh", "Dunne", "Boyd"],
  },
  // 19. Southern Europe (Mediterranean / Italian & Greek)
  {
    region: "Southern Europe (Mediterranean)",
    given: ["Matteo", "Chiara", "Lorenzo", "Elena", "Giancarlo", "Giulia", "Stefano", "Alessia", "Nikos", "Eleni", "Dimitris", "Kalliope", "Thanasis", "Ioanna", "Vassilis"],
    family: ["Rinaldi", "Moretti", "Fontana", "Grecu", "Costantini", "Marchetti", "De Luca", "Papadopoulos", "Economou", "Vassiliou", "Angelopoulos", "Georgiou", "Ferraro", "Serra", "Riva"],
  },
  // 20. Eastern Europe (Slavic)
  {
    region: "Eastern Europe (Slavic)",
    given: ["Marek", "Elena", "Stefan", "Danuta", "Pavel", "Milena", "Irek", "Anya", "Tibor", "Zofia", "Bohdan", "Klara", "Vasil", "Mira", "Stanislav"],
    family: ["Nowak", "Kowalski", "Wisniewski", "Dabrowski", "Vesely", "Kovac", "Horvath", "Nagy", "Popov", "Ivanov", "Stankovic", "Jovanovic", "Petrov", "Sokolov", "Morozov"],
  },
  // 21. Latin America (Andean & Southern Cone)
  {
    region: "Latin America (Andean / Cone)",
    given: ["Inti", "Nayra", "Lautaro", "Kusi", "Lucia", "Camila", "Mateo", "Sol", "Valentin", "Renata", "Joaquin", "Amancay", "Nahuel", "Catalina", "Facundo"],
    family: ["Mamani", "Quispe", "Condori", "Flores", "Huaman", "Chavez", "Navarro", "Morales", "Paredes", "Salazar", "Rojas", "Garrido", "Vargas", "Fuentes", "Barrios"],
  },
  // 22. Latin America (Mesoamerica / Mexican)
  {
    region: "Latin America (Mesoamerica)",
    given: ["Yaretzi", "Cuauhtémoc", "Citlali", "Tenoch", "Xochitl", "Emiliano", "Itzel", "Mateo", "Sofia", "Diego", "Valentina", "Tlaloc", "Renata", "Gael", "Alondra"],
    family: ["Hernandez", "Lopez", "Martinez", "Gonzalez", "Rodriguez", "Perez", "Sanchez", "Ramirez", "Cruz", "Flores", "Gomez", "Morales", "Vazquez", "Reyes", "Jimenez"],
  },
  // 23. Indigenous North America & Arctic (Inuit & Woodland)
  {
    region: "Indigenous North America",
    given: ["Kunu", "Panik", "Uki", "Nanuq", "Sura", "Taqtu", "Kitchi", "Aponi", "Mato", "Nokomis", "Chayton", "Enola", "Huyana", "Ohanzee", "Winona"],
    family: ["Okalik", "Arnakak", "Tagalik", "Karetak", "Angootingmar", "Otoole", "Blackwood", "Runningdeer", "Standingbear", "Crowfeather", "Riverwind", "Ironhorn", "Clearwater", "Hawkwatcher", "Braveheart"],
  },
  // 24. Pacific Islands & Polynesia
  {
    region: "Pacific Islands (Polynesian)",
    given: ["Keanu", "Moana", "Tane", "Leilani", "Kavika", "Aroha", "Manu", "Talia", "Hemi", "Kalani", "Sione", "Mele", "Tamatoa", "Marama", "Vaea"],
    family: ["Tuivasa", "Kahananui", "Faumuina", "Ngapo", "Kamealoha", "Tavita", "Ah-Ching", "Mahuika", "Lavea", "Finau", "Kaufusi", "Paewai", "Pritchard", "Te Kanawa", "Mikaere"],
  },
  // 25. Western Europe (Iberian / Portuguese & Basque)
  {
    region: "Western Europe (Iberian)",
    given: ["Tiago", "Ines", "Goncalo", "Beatriz", "Aitor", "Amaia", "Iker", "Maite", "Unai", "Nerea", "Gorka", "Idoia", "Martim", "Leonor", "Endika"],
    family: ["Silva", "Ferreira", "Santos", "Oliveira", "Pereira", "Rodrigues", "Sousa", "Etxeberria", "Agirre", "Larrazabal", "Zubizarreta", "Mendizabal", "Iturbe", "Garmendia", "Goikoetxea"],
  },
  // 26. Maritime / Anglo Atlantic
  {
    region: "Maritime Atlantic",
    given: ["Julian", "Tobias", "Clara", "Finch", "Rowan", "Silas", "Hannah", "Marcus", "Audrey", "Elena", "Leo", "Nadia", "Caleb", "Mira", "Arthur"],
    family: ["Hawthorne", "Sterling", "Davenport", "Vance", "Mercer", "Thorne", "Callahan", "Finch", "Pendelton", "Drake", "Cael", "Abernathy", "Blythe", "Voss", "Halcyon"],
  },
];

/**
 * Alien name syllables for Strange and Unexpected worlds.
 */
const ALIEN_ONSET = ["Vey", "Zep", "Mor", "Khor", "Xan", "Iri", "Ael", "Thal", "Cael", "Syr", "Nyx", "Onyx", "Darek", "Quil", "Vor", "Pell", "Kae", "Rhaes"];
const ALIEN_NUCLEUS = ["a", "e", "i", "o", "u", "ae", "ei", "ou", "ya", "ia", "or"];
const ALIEN_CODA = ["n", "th", "r", "s", "x", "l", "m", "k", "v", "d", "ss", "sh"];

const ALIEN_EPITHETS = [
  "of the Fold", "of the Chasm", "the Unbound", "the Resonant", "Prism-Caster",
  "Tide-Caller", "of Deep Pressure", "the Weaver", "Vent-Walker", "Sun-Singer",
];

const STELLAR_SYSTEMS = [
  "Aldebaran", "Cygnus", "Vega", "Kepler-186", "Trappist-1", "Fomalhaut",
  "Epsilon Eridani", "Tau Ceti", "Antares", "Spica", "Deneb", "Altair",
  "Arcturus", "Procyon", "Sirius", "Rigel", "Bellatrix", "Alcyone",
];

const STELLAR_TERRAIN = [
  "Orbital Ring", "Dyson Swarm Prime", "High Citadel", "Lattice Station",
  "Heliospheric Spire", "Resonance Hub", "Lagrange Nexus", "Vacuum Scriptorium",
  "Chrono-Array", "Relativistic Pass", "Sub-Stellar Basin", "Noospheric Bastion",
];

const HUMAN_PLACES = [
  "Cedar Ridge Basin", "Oakhaven River Port", "Valle Verde Terrace",
  "High Pass Mill", "Cobalt Harbor", "Mistwood Lowland", "Orchard Hill",
  "Silverstream Creek", "Brickworks Junction", "Westhaven Docks",
  "Tidewater Ford", "Lantern Wharf", "Pinewood Forge", "Bellview Quarry",
];

const ALIEN_PLACES = [
  "Toroidal Chasm", "Heavy-Argon Trench", "Obsidian Rift", "Amber Terrace",
  "Singing Basalt Ridge", "Vapor Spire Colony", "Curved Manifold Valley",
  "Fluctuating Crest", "Pneumatic Shelf", "Crystalline Sinkhole",
  "Resonant Cavern Nine", "Sub-Planar Fold", "Thermal Flume Haven",
];

/**
 * Generate a culturally consistent worldwide human personal name.
 * Avoids any names in the `avoid` set.
 */
export function generatePersonName(rng: () => number, avoid?: Set<string>): string {
  for (let attempt = 0; attempt < 40; attempt++) {
    const pool = pick(WORLD_REGIONAL_POOLS, rng);
    const given = pick(pool.given, rng);
    const family = pick(pool.family, rng);
    const full = `${given} ${family}`;
    if (!avoid || !avoid.has(full)) {
      return full;
    }
  }
  // Fallback compound
  const pool = pick(WORLD_REGIONAL_POOLS, rng);
  return `${pick(pool.given, rng)} ${pick(pool.family, rng)}`;
}

/**
 * Syllable-based alien or non-standard name for Strange / Unexpected timelines.
 */
export function generateAlienName(rng: () => number): string {
  const syllables = 2 + (rng() > 0.65 ? 1 : 0);
  let name = "";
  for (let s = 0; s < syllables; s++) {
    const onset = pick(ALIEN_ONSET, rng);
    const nuc = pick(ALIEN_NUCLEUS, rng);
    const coda = pick(ALIEN_CODA, rng);
    const piece = s === 0 ? `${onset}${nuc}` : `${coda}${nuc}`;
    name += piece.toLowerCase();
  }
  // Capitalize
  name = name.charAt(0).toUpperCase() + name.slice(1);
  if (rng() > 0.6) {
    const epithet = pick(ALIEN_EPITHETS, rng);
    return `${name} ${epithet}`;
  }
  const clan = pick(ALIEN_ONSET, rng);
  return `${name} ${clan}`;
}

/**
 * Generates a descriptive place name tailored to human, stellar, or alien worlds.
 */
export function generatePlaceName(
  rng: () => number,
  flavor: "human" | "stellar" | "alien"
): string {
  if (flavor === "stellar") {
    const star = pick(STELLAR_SYSTEMS, rng);
    const terrain = pick(STELLAR_TERRAIN, rng);
    return `${star} ${terrain}`;
  }
  if (flavor === "alien") {
    return pick(ALIEN_PLACES, rng);
  }
  return pick(HUMAN_PLACES, rng);
}
